"use client";

import { useId, useState, type ReactNode } from "react";
import { playerStandingsPage } from "./final-player-standings";
import styles from "./FinalPlayerStandings.module.css";

export function PostPlayerDisclosure({
  rows,
  openLabel,
  closeLabel,
  emptyLabel,
  paginationLabel,
  previousLabel,
  nextLabel,
  pages,
}: {
  rows: { id: string; content: ReactNode }[];
  openLabel: string;
  closeLabel: string;
  emptyLabel: string;
  paginationLabel: string;
  previousLabel: string;
  nextLabel: string;
  pages: { number: string; label: string; status: string }[];
}) {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const [requestedPage, setPage] = useState(1);
  const { page, totalPages, items } = playerStandingsPage(rows, requestedPage);
  const mobilePageIds = new Set(items.map((row) => row.id));

  return (
    <div className={styles.disclosure}>
      <button
        type="button"
        className={styles.disclosureButton}
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => {
          setExpanded(!expanded);
          setPage(1);
        }}
      >
        {expanded ? closeLabel : openLabel}
        <svg
          className={styles.disclosureChevron}
          width="16"
          height="10"
          viewBox="0 0 16 10"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 2L8 8L14 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div id={id} hidden={!expanded}>
        {rows.length === 0 ? (
          <p className={styles.message}>{emptyLabel}</p>
        ) : (
          <>
            <ul className={styles.remaining}>
              {rows.map((row) => (
                <li
                  key={row.id}
                  className={styles.compactEntry}
                  data-mobile-hidden={!mobilePageIds.has(row.id)}
                >
                  {row.content}
                </li>
              ))}
            </ul>
            <p
              className={styles.pageStatus}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {pages[page - 1]?.status}
            </p>
            {totalPages > 1 && (
              <nav className={styles.pagination} aria-label={paginationLabel}>
                <button
                  className={styles.paginationDirection}
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  aria-controls={id}
                >
                  {previousLabel}
                </button>
                {pages.map((entry, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={entry.label}
                    aria-current={page === index + 1 ? "page" : undefined}
                    aria-controls={id}
                    onClick={() => setPage(index + 1)}
                  >
                    {entry.number}
                  </button>
                ))}
                <button
                  className={styles.paginationDirection}
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  aria-controls={id}
                >
                  {nextLabel}
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
}
